import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";
const BASE_URL = process.env.REACT_APP_BASE_URL;

const buildListQueryParams = (params = {}, searchKeys = ["name"]) => {
  const queryParams = new URLSearchParams();
  const { search, name, page = 1, per_page = 20, ...rest } = params;
  queryParams.set("page", String(page));
  queryParams.set("per_page", String(per_page));
  const searchTerm = String(name || search || "").trim();
  if (searchTerm) {
    queryParams.set("search", searchTerm);
    searchKeys.forEach((key) => queryParams.set(key, searchTerm));
  }
  Object.entries(rest).forEach(([key, value]) => {
    if (
      value !== "" &&
      value != null &&
      key !== "search" &&
      key !== "name" &&
      !searchKeys.includes(key)
    ) {
      queryParams.append(key, String(value));
    }
  });
  return queryParams;
};

// Add transport document
export const addTransportDocument = createAsyncThunk(
  "transportDocuments/add",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/transport-documents`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to add transport document";
      return rejectWithValue({ message });
    }
  }
);

// Fetch single transport document
export const fetchTransportDocument = createAsyncThunk(
  "transportDocuments/fetchOne",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/transport-documents/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
            Lang: i18n.language,
          },
        }
      );
      return response.data.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load transport document";
      return rejectWithValue({ message });
    }
  }
);

// Update transport document
export const updateTransportDocument = createAsyncThunk(
  "transportDocuments/update",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/transport-documents/update/${id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to update transport document";
      return rejectWithValue({ message });
    }
  }
);

// Close transport document
export const closeTransportDocument = createAsyncThunk(
  "transportDocuments/close",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/transport-documents/close/${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to close transport document";
      return rejectWithValue({ message });
    }
  }
);

// Cancel transport document
export const cancelTransportDocument = createAsyncThunk(
  "transportDocuments/cancel",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/transport-documents/cancel/${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to cancel transport document";
      return rejectWithValue({ message });
    }
  }
);

// Fetch cities
export const fetchCities = createAsyncThunk(
  "transportDocuments/fetchCities",
  async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        if (params[key] !== "" && params[key] !== null && params[key] !== undefined) {
          queryParams.append(key, params[key]);
        }
      });
      const url = `${BASE_URL}/settings/cities${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data || [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load cities";
      throw new Error(message);
    }
  }
);

// Fetch clients
export const fetchClients = createAsyncThunk(
  "transportDocuments/fetchClients",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = buildListQueryParams(params, ["name"]);
      const url = `${BASE_URL}/clients?${queryParams.toString()}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load clients";
      return rejectWithValue({ message });
    }
  }
);

// Fetch business sectors
export const fetchBusinessSectors = createAsyncThunk(
  "transportDocuments/fetchBusinessSectors",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = buildListQueryParams(params, ["name", "legal_name"]);
      const url = `${BASE_URL}/business-sectors?${queryParams.toString()}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load business sectors";
      return rejectWithValue({ message });
    }
  }
);

// Fetch winch partners
export const fetchWinchPartners = createAsyncThunk(
  "transportDocuments/fetchWinchPartners",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      const { search, page = 1, per_page = 20, ...rest } = params;
      queryParams.set("page", String(page));
      queryParams.set("per_page", String(per_page));
      if (search) queryParams.set("search", String(search));
      Object.entries(rest).forEach(([key, value]) => {
        if (value !== "" && value != null && key !== "search") {
          queryParams.append(key, String(value));
        }
      });
      const url = `${BASE_URL}/partners?${queryParams.toString()}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load winch partners";
      return rejectWithValue({ message });
    }
  }
);

// Fetch drivers
export const fetchDrivers = createAsyncThunk(
  "transportDocuments/fetchDrivers",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      const { search, name, page = 1, per_page = 20, ...rest } = params;
      queryParams.set("page", String(page));
      queryParams.set("per_page", String(per_page));
      const searchTerm = String(name || search || "").trim();
      if (searchTerm) {
        queryParams.set("search", searchTerm);
      }
      Object.entries(rest).forEach(([key, value]) => {
        if (value !== "" && value != null && key !== "search" && key !== "name") {
          queryParams.append(key, String(value));
        }
      });
      const url = `${BASE_URL}/drivers?${queryParams.toString()}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load drivers";
      return rejectWithValue({ message });
    }
  }
);

// Fetch vehicles (fleet list)
export const fetchVehicles = createAsyncThunk(
  "transportDocuments/fetchVehicles",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      const { search, page = 1, per_page = 20, ...rest } = params;
      queryParams.set("page", String(page));
      queryParams.set("per_page", String(per_page));
      if (search) queryParams.set("search", String(search));
      Object.entries(rest).forEach(([key, value]) => {
        if (value !== "" && value != null && key !== "search") {
          queryParams.append(key, String(value));
        }
      });
      const url = `${BASE_URL}/vehicles?${queryParams.toString()}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load vehicles";
      return rejectWithValue({ message });
    }
  }
);

// Fetch service types (for service_type_id)
export const fetchServiceTypes = createAsyncThunk(
  "transportDocuments/fetchServiceTypes",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      const { search, page = 1, per_page = 20, ...rest } = params;
      queryParams.set("page", String(page));
      queryParams.set("per_page", String(per_page));
      if (search) queryParams.set("search", String(search));
      Object.entries(rest).forEach(([key, value]) => {
        if (value !== "" && value != null && key !== "search") {
          queryParams.append(key, String(value));
        }
      });
      const url = `${BASE_URL}/service-types?${queryParams.toString()}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load service types";
      return rejectWithValue({ message });
    }
  }
);

// Fetch plate types
export const fetchPlateTypes = createAsyncThunk(
  "transportDocuments/fetchPlateTypes",
  async () => {
    try {
      const response = await axios.get(`${BASE_URL}/settings/plates-types`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data || [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load plate types";
      throw new Error(message);
    }
  }
);

// Fetch business sector users
export const fetchBusinessSectorUsers = createAsyncThunk(
  "transportDocuments/fetchBusinessSectorUsers",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        if (params[key] !== "" && params[key] !== null && params[key] !== undefined) {
          queryParams.append(key, params[key]);
        }
      });
      const url = `${BASE_URL}/business-sector-users${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load business sector users";
      return rejectWithValue({ message });
    }
  }
);

// Fetch quotation request customer by customer id
export const fetchQuotationRequestCustomerByCustomer = createAsyncThunk(
  "transportDocuments/fetchQuotationRequestCustomerByCustomer",
  async (customerId) => {
    try {
      const response = await axios.get(`${BASE_URL}/quotations-request-customers/customer/${customerId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load quotation request customer";
      throw new Error(message);
    }
  }
);

/** UI customer_type → show-customer API param */
export const mapCustomerTypeToShowQuotationApi = (customerType) => {
  if (customerType === "Business Sector") return "Business Sector";
  return "Individual Customer";
};

const collectTransportLocationCityIds = (locations = []) =>
  (locations || [])
    .map((loc) => String(loc?.city_id ?? loc ?? "").trim())
    .filter(Boolean);

/** Build query string for GET /quotations-request-customers/show-customer (new city_id/city_type format). */
export const buildShowCustomerQuotationSearchParams = ({
  customerType,
  customerId,
  serviceId,
  loadingLocations = [],
  unloadingLocations = [],
  cityFrom,
  cityTo,
  quotationType = "Travel",
} = {}) => {
  const params = new URLSearchParams();
  params.append("service_type", quotationType);
  params.append("customer_type", mapCustomerTypeToShowQuotationApi(customerType));
  params.append("customer_id", String(customerId));
  params.append("service_id", String(serviceId));

  let loadingIds = collectTransportLocationCityIds(loadingLocations);
  let unloadingIds = collectTransportLocationCityIds(unloadingLocations);
  if (!loadingIds.length && cityFrom != null && String(cityFrom).trim() !== "") {
    loadingIds = [String(cityFrom).trim()];
  }
  if (!unloadingIds.length && cityTo != null && String(cityTo).trim() !== "") {
    unloadingIds = [String(cityTo).trim()];
  }

  const maxLen = Math.max(loadingIds.length, unloadingIds.length);
  let cityIndex = 0;
  for (let i = 0; i < maxLen; i += 1) {
    if (loadingIds[i]) {
      params.append(`city_id[${cityIndex}]`, loadingIds[i]);
      params.append(`city_type[${cityIndex}]`, "from");
      cityIndex += 1;
    }
    if (unloadingIds[i]) {
      params.append(`city_id[${cityIndex}]`, unloadingIds[i]);
      params.append(`city_type[${cityIndex}]`, "to");
      cityIndex += 1;
    }
  }

  return params;
};

// Fetch quotation request customer for travel (show-customer)
export const fetchQuotationRequestCustomerByTravel = createAsyncThunk(
  "transportDocuments/fetchQuotationRequestCustomerByTravel",
  async (
    {
      customerType,
      customerId,
      serviceId,
      loadingLocations = [],
      unloadingLocations = [],
      cityFrom,
      cityTo,
      quotationType = "Travel",
    },
    { rejectWithValue }
  ) => {
    try {
      const params = buildShowCustomerQuotationSearchParams({
        customerType,
        customerId,
        serviceId,
        loadingLocations,
        unloadingLocations,
        cityFrom,
        cityTo,
        quotationType,
      });
      const response = await axios.get(
        `${BASE_URL}/quotations-request-customers/show-customer?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
            Lang: i18n.language,
          },
        }
      );
      return response.data.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "Failed to load quotation request customer";
      return rejectWithValue({ message });
    }
  }
);

/** Build query string for GET /quotations-request-suppliers/show-supplier */
export const buildShowSupplierQuotationSearchParams = ({
  partnerId,
  serviceId,
  loadingLocations = [],
  unloadingLocations = [],
  cityFrom,
  cityTo,
  quotationType = "Travel",
} = {}) => {
  const params = new URLSearchParams();
  params.append("service_type", quotationType);
  params.append("partner_id", String(partnerId));
  params.append("service_id", String(serviceId));

  let loadingIds = collectTransportLocationCityIds(loadingLocations);
  let unloadingIds = collectTransportLocationCityIds(unloadingLocations);
  if (!loadingIds.length && cityFrom != null && String(cityFrom).trim() !== "") {
    loadingIds = [String(cityFrom).trim()];
  }
  if (!unloadingIds.length && cityTo != null && String(cityTo).trim() !== "") {
    unloadingIds = [String(cityTo).trim()];
  }

  const maxLen = Math.max(loadingIds.length, unloadingIds.length);
  let cityIndex = 0;
  for (let i = 0; i < maxLen; i += 1) {
    if (loadingIds[i]) {
      params.append(`city_id[${cityIndex}]`, loadingIds[i]);
      params.append(`city_type[${cityIndex}]`, "from");
      cityIndex += 1;
    }
    if (unloadingIds[i]) {
      params.append(`city_id[${cityIndex}]`, unloadingIds[i]);
      params.append(`city_type[${cityIndex}]`, "to");
      cityIndex += 1;
    }
  }

  return params;
};

// Fetch quotation request supplier for travel (show-supplier)
export const fetchQuotationRequestSupplierByTravel = createAsyncThunk(
  "transportDocuments/fetchQuotationRequestSupplierByTravel",
  async (
    {
      partnerId,
      serviceId,
      loadingLocations = [],
      unloadingLocations = [],
      cityFrom,
      cityTo,
      quotationType = "Travel",
    },
    { rejectWithValue }
  ) => {
    try {
      const params = buildShowSupplierQuotationSearchParams({
        partnerId,
        serviceId,
        loadingLocations,
        unloadingLocations,
        cityFrom,
        cityTo,
        quotationType,
      });
      const response = await axios.get(
        `${BASE_URL}/quotations-request-suppliers/show-supplier?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
            Lang: i18n.language,
          },
        }
      );
      return response.data.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "Failed to load quotation request supplier";
      return rejectWithValue({ message });
    }
  }
);
export const fetchLoadTypes = createAsyncThunk(
  "transportDocuments/fetchLoadTypes",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      const page = params.page ?? 1;
      const per_page = params.per_page ?? 20;
      const search = String(params.search ?? "").trim();
      queryParams.set("page", String(page));
      queryParams.set("per_page", String(per_page));
      if (search) {
        queryParams.set("search", search);
      }
      const url = `${BASE_URL}/load-types?${queryParams.toString()}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load load types";
      return rejectWithValue({ message });
    }
  }
);

// Fetch city details (for coordinates)
export const fetchCityDetails = createAsyncThunk(
  "transportDocuments/fetchCityDetails",
  async (id) => {
    try {
      const response = await axios.get(`${BASE_URL}/cities/show/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load city details";
      throw new Error(message);
    }
  }
);

// Fetch transport documents list
export const fetchTransportDocuments = createAsyncThunk(
  "transportDocuments/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        if (params[key] !== "" && params[key] !== null && params[key] !== undefined) {
          queryParams.append(key, params[key]);
        }
      });
      const url = `${BASE_URL}/transport-documents${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load transport documents";
      return rejectWithValue({ message });
    }
  }
);

const TransportDocumentsSlice = createSlice({
  name: "transportDocuments",
  initialState: {
    isLoading: false,
    error: null,
    success: false,
    cities: [],
    citiesPagination: null,
    clients: [],
    clientsPagination: null,
    businessSectors: [],
    businessSectorsPagination: null,
    businessSectorUsers: [],
    businessSectorUsersPagination: null,
    winchPartners: [],
    winchPartnersPagination: null,
    winchPartnersFilterLoading: false,
    drivers: [],
    driversPagination: null,
    driversFilterLoading: false,
    vehicles: [],
    vehiclesPagination: null,
    vehiclesFilterLoading: false,
    serviceTypes: [],
    serviceTypesPagination: null,
    serviceTypesFilterLoading: false,
    loadTypes: [],
    loadTypesPagination: null,
    plateTypes: [],
    cityDetails: null,
    quotationRequestCustomer: null,
    transportDocuments: [],
    pagination: null,
    record: null,
    lastCreatedId: null,
  },
  reducers: {
    clearState: (state) => {
      state.isLoading = false;
      state.error = null;
      state.success = false;
      state.lastCreatedId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Add transport document
      .addCase(addTransportDocument.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
        state.lastCreatedId = null;
      })
      .addCase(addTransportDocument.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "Transport document added successfully";
        state.error = null;
        const payloadData = action.payload?.data ?? action.payload;
        state.lastCreatedId =
          payloadData?.id ??
          payloadData?.transport_document_id ??
          payloadData?.transport_document?.id ??
          payloadData?.document?.id ??
          action.payload?.id ??
          (Array.isArray(payloadData) ? payloadData[0]?.id : null) ??
          null;
      })
      .addCase(addTransportDocument.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Failed to add transport document";
        state.success = false;
      })
      // Fetch cities
      .addCase(fetchCities.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchCities.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cities = action.payload || [];
      })
      .addCase(fetchCities.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to load cities";
      })
      // Fetch clients
      .addCase(fetchClients.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.isLoading = false;
        const page = action.meta.arg?.page || 1;
        if (page === 1) {
          // First page - replace data
          state.clients = action.payload?.data?.data || [];
        } else {
          // Subsequent pages - append data
          state.clients = [...(state.clients || []), ...(action.payload?.data?.data || [])];
        }
        state.clientsPagination = action.payload?.data?.pagination || null;
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Failed to load clients";
      })
      // Fetch business sectors
      .addCase(fetchBusinessSectors.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchBusinessSectors.fulfilled, (state, action) => {
        state.isLoading = false;
        const page = action.meta.arg?.page || 1;
        if (page === 1) {
          // First page - replace data
          state.businessSectors = action.payload?.data?.data || [];
        } else {
          // Subsequent pages - append data
          state.businessSectors = [...(state.businessSectors || []), ...(action.payload?.data?.data || [])];
        }
        state.businessSectorsPagination = action.payload?.data?.pagination || null;
      })
      .addCase(fetchBusinessSectors.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Failed to load business sectors";
      })
      // Fetch winch partners
      .addCase(fetchWinchPartners.pending, (state) => {
        state.winchPartnersFilterLoading = true;
      })
      .addCase(fetchWinchPartners.fulfilled, (state, action) => {
        state.winchPartnersFilterLoading = false;
        const page = action.meta.arg?.page || 1;
        const newData = action.payload?.data || [];
        if (page === 1) {
          state.winchPartners = newData;
        } else {
          const prev = Array.isArray(state.winchPartners) ? state.winchPartners : [];
          const ids = new Set(prev.map((r) => r.id));
          state.winchPartners = [
            ...prev,
            ...newData.filter((r) => r?.id != null && !ids.has(r.id)),
          ];
        }
        state.winchPartnersPagination = action.payload?.pagination || null;
      })
      .addCase(fetchWinchPartners.rejected, (state, action) => {
        state.winchPartnersFilterLoading = false;
        state.error = action.payload?.message || action.error?.message || "Failed to load winch partners";
      })
      // Fetch drivers
      .addCase(fetchDrivers.pending, (state) => {
        state.driversFilterLoading = true;
      })
      .addCase(fetchDrivers.fulfilled, (state, action) => {
        state.driversFilterLoading = false;
        const page = action.meta.arg?.page || 1;
        const newData = action.payload?.data?.data || [];
        if (page === 1) {
          state.drivers = newData;
        } else {
          const prev = Array.isArray(state.drivers) ? state.drivers : [];
          const ids = new Set(prev.map((r) => r.id));
          state.drivers = [
            ...prev,
            ...newData.filter((r) => r?.id != null && !ids.has(r.id)),
          ];
        }
        state.driversPagination = action.payload?.data?.pagination || null;
      })
      .addCase(fetchDrivers.rejected, (state, action) => {
        state.driversFilterLoading = false;
        state.error = action.payload?.message || action.error?.message || "Failed to load drivers";
      })
      // Fetch vehicles
      .addCase(fetchVehicles.pending, (state) => {
        state.vehiclesFilterLoading = true;
      })
      .addCase(fetchVehicles.fulfilled, (state, action) => {
        state.vehiclesFilterLoading = false;
        const page = action.meta.arg?.page || 1;
        const newData = action.payload?.data?.data || [];
        if (page === 1) {
          state.vehicles = newData;
        } else {
          const prev = Array.isArray(state.vehicles) ? state.vehicles : [];
          const ids = new Set(prev.map((r) => r.id));
          state.vehicles = [
            ...prev,
            ...newData.filter((r) => r?.id != null && !ids.has(r.id)),
          ];
        }
        state.vehiclesPagination = action.payload?.data?.pagination || null;
      })
      .addCase(fetchVehicles.rejected, (state, action) => {
        state.vehiclesFilterLoading = false;
        state.error = action.payload?.message || action.error?.message || "Failed to load vehicles";
      })
      // Fetch service types
      .addCase(fetchServiceTypes.pending, (state) => {
        state.serviceTypesFilterLoading = true;
      })
      .addCase(fetchServiceTypes.fulfilled, (state, action) => {
        state.serviceTypesFilterLoading = false;
        const page = action.meta.arg?.page || 1;
        const newData = action.payload?.data?.data || [];
        if (page === 1) {
          state.serviceTypes = newData;
        } else {
          const prev = Array.isArray(state.serviceTypes) ? state.serviceTypes : [];
          const ids = new Set(prev.map((r) => r.id));
          state.serviceTypes = [
            ...prev,
            ...newData.filter((r) => r?.id != null && !ids.has(r.id)),
          ];
        }
        state.serviceTypesPagination = action.payload?.data?.pagination || null;
      })
      .addCase(fetchServiceTypes.rejected, (state, action) => {
        state.serviceTypesFilterLoading = false;
        state.error = action.payload?.message || "Failed to load service types";
      })
      // Fetch load types
      .addCase(fetchLoadTypes.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchLoadTypes.fulfilled, (state, action) => {
        state.isLoading = false;
        const page = action.meta.arg?.page || 1;
        if (page === 1) {
          state.loadTypes = action.payload?.data?.data || [];
        } else {
          state.loadTypes = [...(state.loadTypes || []), ...(action.payload?.data?.data || [])];
        }
        state.loadTypesPagination = action.payload?.data?.pagination || null;
      })
      .addCase(fetchLoadTypes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Failed to load load types";
      })
      // Fetch city details
      .addCase(fetchCityDetails.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchCityDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cityDetails = action.payload;
      })
      .addCase(fetchCityDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to load city details";
      })
      // Fetch plate types
      .addCase(fetchPlateTypes.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchPlateTypes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.plateTypes = action.payload || [];
      })
      .addCase(fetchPlateTypes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to load plate types";
      })
      // Fetch business sector users
      .addCase(fetchBusinessSectorUsers.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchBusinessSectorUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        const page = action.meta.arg?.page || 1;
        if (page === 1) {
          // First page - replace data
          state.businessSectorUsers = action.payload?.data?.data || [];
        } else {
          // Subsequent pages - append data
          state.businessSectorUsers = [...(state.businessSectorUsers || []), ...(action.payload?.data?.data || [])];
        }
        state.businessSectorUsersPagination = action.payload?.data?.pagination || null;
      })
      .addCase(fetchBusinessSectorUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Failed to load business sector users";
      })
      // Fetch quotation request customer by customer
      .addCase(fetchQuotationRequestCustomerByCustomer.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchQuotationRequestCustomerByCustomer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.quotationRequestCustomer = action.payload;
      })
      .addCase(fetchQuotationRequestCustomerByCustomer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to load quotation request customer";
      })
      // Fetch transport documents list
      .addCase(fetchTransportDocuments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTransportDocuments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.transportDocuments = action.payload?.data?.data || [];
        state.pagination = action.payload?.data?.pagination || null;
      })
      .addCase(fetchTransportDocuments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Failed to load transport documents";
      })
      // Fetch single transport document
      .addCase(fetchTransportDocument.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTransportDocument.fulfilled, (state, action) => {
        state.isLoading = false;
        state.record = action.payload;
      })
      .addCase(fetchTransportDocument.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Failed to load transport document";
      })
      // Update transport document
      .addCase(updateTransportDocument.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateTransportDocument.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || (i18n.language === "ar" ? "تم التحديث بنجاح" : "Updated successfully");
      })
      .addCase(updateTransportDocument.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Failed to update transport document";
      })
      .addCase(closeTransportDocument.fulfilled, (state, action) => {
        state.success =
          action.payload?.message ||
          (i18n.language === "ar" ? "تم غلق الوثيقة بنجاح" : "Document closed successfully");
        if (action.payload?.data) {
          state.record = action.payload.data;
        } else if (state.record) {
          state.record = { ...state.record, status: "closed" };
        }
      })
      .addCase(closeTransportDocument.rejected, (state, action) => {
        state.error =
          action.payload?.message || "Failed to close transport document";
      })
      .addCase(cancelTransportDocument.fulfilled, (state, action) => {
        state.success =
          action.payload?.message ||
          (i18n.language === "ar" ? "تم إلغاء الوثيقة بنجاح" : "Document cancelled successfully");
        if (action.payload?.data) {
          state.record = action.payload.data;
        } else if (state.record) {
          state.record = { ...state.record, status: "cancelled" };
        }
      })
      .addCase(cancelTransportDocument.rejected, (state, action) => {
        state.error =
          action.payload?.message || "Failed to cancel transport document";
      });
  },
});

export const { clearState } = TransportDocumentsSlice.actions;
export default TransportDocumentsSlice.reducer;

